/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from 'react';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleButton from '../common/TrebbleButton';
import ModalHeader from '../common/ModalHeader';
import SelectableOptionCard from './SelectableOptionCard';
import StripeConfig from '../../models/helper/StripeConfig';
import WorkspacePricingCatalog from '../../models/helper/WorkspacePricingCatalog';

function formatHourPackPriceDisplay(price, currency) {
    const rounded = Math.round(Number(price) * 100) / 100;
    const prefix = (currency || 'usd').toLowerCase() === 'cad' ? 'CA$' : '$';
    return prefix + rounded.toFixed(2);
}

function getLabelKeyForHours(hours) {
    // Known label keys for standard pack sizes. For unknown sizes fall back to
    // the 10-hour key — the displayed copy is generic enough that we prefer
    // showing the nearest known label over a blank string.
    if (hours === 5) return 'PACK_5_HOURS_LABEL';
    if (hours === 10) return 'PACK_10_HOURS_LABEL';
    if (hours === 20) return 'PACK_20_HOURS_LABEL';
    return 'PACK_10_HOURS_LABEL';
}

function getBadgeKeyForHours(hours) {
    if (hours === 10) return 'MOST_POPULAR';
    if (hours === 20) return 'BEST_VALUE';
    return null;
}

// Fallback hour pack catalog used until /api/v1/billing/plans resolves or if
// it is unavailable. Authoritative prices live in Stripe. Prices are sourced
// from models/helper/WorkspacePricingCatalog.js so every billing surface
// (Change Plan, Upgrade-to-Team, Hour Packs) stays in sync.
const FALLBACK_HOUR_PACKS = WorkspacePricingCatalog.FALLBACK_HOUR_PACKS.map(function (pack) {
    return {
        packSize: pack.packSize,
        labelKey: getLabelKeyForHours(pack.packSize),
        price: pack.price,
        currency: pack.currency,
        badgeKey: getBadgeKeyForHours(pack.packSize),
    };
});

// Re-exported for back-compat with any consumers that imported HOUR_PACKS.
const HOUR_PACKS = FALLBACK_HOUR_PACKS;

function formatHours(value) {
    if (value == null || isNaN(value)) return '0';
    var num = Number(value);
    if (Number.isInteger(num)) return String(num);
    return (Math.round(num * 10) / 10).toString();
}

const HourPackContent = ({
    workspaceUsage,
    onPurchase,
    onCancel,
    i18nModule,
    useEditorTheme = true,
}) => {
    const t = (key) => window.getI18n(i18nModule, key);

    const usage = workspaceUsage || {};
    const currentHoursUsed = Number(usage.hoursUsed || 0);
    const currentHoursTotal = usage.hoursTotal == null ? null : Number(usage.hoursTotal);

    const [selectedPackSize, setSelectedPackSize] = useState(10);
    const [submitting, setSubmitting] = useState(false);
    const [hourPacks, setHourPacks] = useState(FALLBACK_HOUR_PACKS);

    useEffect(() => {
        let cancelled = false;
        if (!StripeConfig || typeof StripeConfig.fetchPricingCatalog !== 'function') {
            return () => {};
        }
        StripeConfig.fetchPricingCatalog().then((catalog) => {
            if (cancelled || !catalog || !catalog.addOns || !Array.isArray(catalog.addOns.hourPacks)) return;
            const backendPacks = catalog.addOns.hourPacks
                .filter((hp) => hp && typeof hp.hours === 'number' && typeof hp.amount === 'number')
                .map((hp) => {
                    const priceDollars = Math.round(Number(hp.amount)) / 100;
                    const currency = (hp.currency || 'usd').toLowerCase();
                    return {
                        packSize: Number(hp.hours),
                        labelKey: getLabelKeyForHours(Number(hp.hours)),
                        price: priceDollars,
                        currency: currency,
                        badgeKey: getBadgeKeyForHours(Number(hp.hours)),
                    };
                });
            if (backendPacks.length > 0) {
                backendPacks.sort((a, b) => a.packSize - b.packSize);
                setHourPacks(backendPacks);
            }
        }).catch((err) => {
            console.warn('HourPackContent: pricing catalog fetch failed, using fallback', err);
        });
        return () => { cancelled = true; };
    }, []);

    // Selected pack MUST match the user's last click (selectedPackSize). If the
    // backend catalog doesn't include that size, we don't silently switch —
    // leave selectedPack null so the Buy button disables and the UI stays in
    // sync with what the user sees highlighted.
    const selectedPack = useMemo(
        () => hourPacks.find((p) => p.packSize === selectedPackSize) || null,
        [selectedPackSize, hourPacks]
    );

    // When the catalog loads (or changes), make sure selectedPackSize is still
    // a valid choice. If the user's previously-selected size vanished (e.g.
    // backend retired the 10h pack), snap to the first available pack so
    // there's always a visible selection that matches the button's payload.
    React.useEffect(() => {
        if (hourPacks.length === 0) return;
        if (!hourPacks.find((p) => p.packSize === selectedPackSize)) {
            setSelectedPackSize(hourPacks[0].packSize);
        }
    }, [hourPacks]);

    const handleConfirm = async () => {
        if (submitting || !selectedPack) return;
        setSubmitting(true);
        try {
            await onPurchase(selectedPack.packSize);
        } finally {
            setSubmitting(false);
        }
    };

    const projectedHoursTotal =
        currentHoursTotal != null && selectedPack ? currentHoursTotal + selectedPack.packSize : null;
    const hourSuffix = (n) =>
        Math.abs(n) === 1 ? t('HOUR_ABBREVIATION') : t('HOURS_ABBREVIATION');

    return (
        <TrebbleCard useEditorTheme={useEditorTheme} style={{ padding: '24px', borderRadius: 0 }}>
            <ModalHeader
                title={t('TITLE')}
                subtitle={t('SUBTITLE')}
                useEditorTheme={useEditorTheme}
            />

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '12px',
                    marginBottom: '20px',
                }}
            >
                {hourPacks.map((pack) => {
                    const isSelected = pack.packSize === selectedPackSize;
                    const perHour = pack.packSize > 0 ? Math.round((pack.price / pack.packSize) * 100) / 100 : 0;
                    return (
                        <SelectableOptionCard
                            key={pack.packSize}
                            selected={isSelected}
                            onSelect={() => setSelectedPackSize(pack.packSize)}
                            badge={pack.badgeKey ? t(pack.badgeKey) : null}
                        >
                            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
                                {t(pack.labelKey)}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>
                                {formatHourPackPriceDisplay(pack.price, pack.currency)}
                            </div>
                            <div style={{ fontSize: '11px', opacity: 0.6 }}>
                                {formatHourPackPriceDisplay(perHour, pack.currency)} {t('PER_HOUR')}
                            </div>
                        </SelectableOptionCard>
                    );
                })}
            </div>

            {selectedPack && (
                <div
                    style={{
                        padding: '12px',
                        border: '1px solid rgba(255,255,255,0.18)',
                        background: 'rgba(255,255,255,0.04)',
                        marginBottom: '16px',
                        fontSize: '13px',
                    }}
                >
                    <div style={{ marginBottom: '6px' }}>
                        <span style={{ opacity: 0.75 }}>{t('CURRENT_USAGE_LABEL')}: </span>
                        <strong>
                            {formatHours(currentHoursUsed)}
                            {currentHoursTotal != null
                                ? ' / ' + formatHours(currentHoursTotal)
                                : ''}{' '}
                            {hourSuffix(currentHoursUsed)}
                        </strong>
                    </div>
                    <div>
                        <span style={{ opacity: 0.75 }}>{t('AFTER_PURCHASE_LABEL')}: </span>
                        <strong>
                            {formatHours(currentHoursUsed)}
                            {projectedHoursTotal != null
                                ? ' / ' + formatHours(projectedHoursTotal)
                                : ''}{' '}
                            {hourSuffix(projectedHoursTotal != null ? projectedHoursTotal : currentHoursUsed)}
                        </strong>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <TrebbleButton onClick={onCancel} useEditorTheme={useEditorTheme} disabled={submitting}>
                    {t('CANCEL_BUTTON')}
                </TrebbleButton>
                <TrebbleButton
                    type="primary"
                    onClick={handleConfirm}
                    useEditorTheme={useEditorTheme}
                    disabled={submitting || !selectedPack}
                    loading={submitting}
                >
                    {t('BUY_BUTTON')}
                </TrebbleButton>
            </div>
        </TrebbleCard>
    );
};

export default HourPackContent;
export { HourPackContent, HOUR_PACKS };
